import type IProduct from "@api/models/IProduct";
import type { AppRegion } from "@core-types/region.enum";
import useAppTranslation from "@hooks/language/useAppTranslation";
import type { IRankedSmurfCardItem } from "@modules/ranked/models/IRankedSmurf.models";
import type ISkin from "@modules/skins/shared/models/ISkin.model";
import type ValorantProduct from "@modules/valorant/types/ValorantProduct";
import type { ExtendedBasketAccount } from "../models/IOrder";
import { EGame } from "../stores/models/EGame";
import { EItemReason } from "../stores/models/EItemReason";
import type IBasketAccount from "../stores/models/IBasketItem.models";

// section start - LoL products

export const useLoLRankedAsBasketAccount = (
  account: IRankedSmurfCardItem,
): IBasketAccount => {
  const { t } = useAppTranslation();

  return {
    price: account.product.price,
    imageUrl: account.rankedInformation.currentRankInformation.emblem,
    region: account.region,
    prefix: t("components.rankedCard.title", {
      region: account.region,
      rank: t(
        `products.lol.${account.rankedInformation.currentRankInformation.rank}.rank`,
        { subRank: account.rankedInformation.currentRankInformation.subRank },
      ),
    }),
    i18nKey: "pages.ranked",
    game: EGame.LoL,
    reason: EItemReason.RANKED,
    accountId: account.id,
    productId: account.product.id,
  };
};

export const useLoLSkinProductAsBasketAccount = (
  product: IProduct,
  skin: ISkin,
  accountId: number,
  region: AppRegion,
): IBasketAccount => {
  return {
    price: product?.price,
    prefix: `${region} ${skin.name}`,
    i18nKey: product.i18nKey,
    region,
    game: EGame.LoL,
    imageUrl: skin.thumbnail,
    accountId,
    reason: EItemReason.SKIN,
    productId: product.id,
    skinId: skin.id,
  };
};

export const useLoLProductAsBasketAccount = (
  product: IProduct,
  accountId: number,
  region: AppRegion,
): IBasketAccount => {
  return {
    price: product.price,
    prefix: region,
    i18nKey: product.i18nKey,
    region,
    imageUrl: product.thumbnail,
    accountId: accountId,
    game: EGame.LoL,
    reason: EItemReason.PRODUCT,
    productId: product.id,
  };
};

// section end - LoL products

// section start - Valorant products

export const useValorantProductAsBasketAccount = (
  product: ValorantProduct,
  accountId: number,
): IBasketAccount => {
  return {
    accountId,
    price: product.price,
    imageUrl: product.thumbnail,
    prefix: product.region,
    i18nKey: product.i18nKey,
    region: product.region,
    game: EGame.VALORANT,
    reason: EItemReason.PRODUCT,
    productId: product.id,
  };
};

// section end - Valorant products

export const useOrderItemAsBasketAccount = (
  account: ExtendedBasketAccount,
): IBasketAccount => {
  const { password, username, ...rest } = account;

  return rest;
};
